/**
 * @m8t-stack/brain-engine — the Foundry-backed DreamModelClient.
 *
 * Extracted from the CLI (apps/cli/.../dream/run.ts) so the CLI and the gateway
 * timer share ONE adapter (DRY). A PLAIN model call (not an agent
 * invoke): `responses.create({ model, input, stream:true })` over the project's
 * OpenAI client (`AIProjectClient.getOpenAIClient()`), collecting the streamed
 * `output_text` deltas and the final token usage. Streaming is doctrine for
 * every model call; the >=5-attempt retry-with-backoff lives in the engine's
 * `propose` (it calls `complete` up to MAX_MODEL_ATTEMPTS), so this client does
 * ONE streamed call per invocation.
 */
import type { DreamModelClient } from "./types";

/** The narrow `getOpenAIClient()` slice we depend on — a streaming `responses.create`. */
export interface OpenAIResponsesLike {
  responses: {
    create: (
      args: Record<string, unknown>,
      extra?: { signal?: AbortSignal },
    ) => Promise<AsyncIterable<ResponseStreamEvent>>;
  };
}

/** A streamed `responses.create` event we read (output text deltas + final usage). */
export interface ResponseStreamEvent {
  type?: string;
  delta?: string;
  response?: { usage?: { input_tokens?: number; output_tokens?: number } };
}

/**
 * `openai` is typed `unknown` on purpose: the `@azure/ai-projects`
 * `getOpenAIClient()` return type overloads `responses.create` to a non-stream
 * `Response`, so the streaming shape we use can't be expressed against it
 * without a cast. Callers pass `getOpenAIClient()` directly; the body bridges to
 * the narrow streaming `OpenAIResponsesLike` slice (the same `as unknown` cast
 * the CLI used before this was extracted). Keeping it `unknown` also avoids an
 * `@azure/ai-projects` dependency in the engine.
 */
export function makeFoundryModelClient(
  openai: unknown,
  model: string,
): DreamModelClient {
  return {
    async complete({ system, user, signal }) {
      const create = (openai as OpenAIResponsesLike).responses.create;
      const stream = await create(
        {
          model,
          // Explicit `type: "message"` — the Responses API rejects untyped input
          // items (400 `Invalid value: ''`).
          input: [
            { type: "message", role: "system", content: system },
            { type: "message", role: "user", content: user },
          ],
          stream: true,
        },
        signal ? { signal } : undefined,
      );
      let text = "";
      let inputTokens = 0;
      let outputTokens = 0;
      for await (const ev of stream) {
        if (ev.type === "response.output_text.delta" && typeof ev.delta === "string") {
          text += ev.delta;
        } else if (ev.type === "response.completed" && ev.response?.usage) {
          inputTokens = ev.response.usage.input_tokens ?? 0;
          outputTokens = ev.response.usage.output_tokens ?? 0;
        }
      }
      return { text, inputTokens, outputTokens };
    },
  };
}
